import _ from 'underscore'
import React from 'react'
import { Hourglass, Inbox, Mail } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import Customer from './customers/show'
import EnquiryLabelsPartial from './labels/partial'
import CoachbitOrders from '../coachbit_orders/index'
import toastr from '@/lib/notifications'
import CallButton from './buttons/call'
import SmsButton from './buttons/sms'
import Smser from '../smser'
import Modal from '../modal'
import EnableAutomaticTrackButton from './buttons/enable_automatic_track'
import DisableAutomaticTrackButton from './buttons/disable_automatic_track'
import DisablePriorityButton from './buttons/disable_priority'
import PipelineStatusButton from './buttons/pipeline_status'
import { Tabs, TabsTrigger } from '@/components/ui/tabs'
import { ScrollableTabsList } from '@/components/ui/scrollable-tabs-list'
import CalendarTime from '../calendar_time'
import LocalTime from '../local_time'
import WhatsappButton from '../whatsapper/whatsapp_button'
import CallOutcome from '../lead/call_outcome'
import EmailButton from '../emailer/email_button'
import SvgIcon from '../svg_icon'
import store from '../../state/store'
import CountryFlag from '@/components/CountryFlag'

class EnquiryHeader extends React.Component {

  onTabClick = (value) => {
    if (value.indexOf('lp') === 0) {
      const lesson_plan_id = value.substring(2)
      store.emit('ui:enquiry:lesson_plan_id', lesson_plan_id)
    }

    store.emit('ui:enquiry:tab_key', value)
  }

  onIpAddressClick = () => {
    store.emit('enquiry:lookup_ip', this.props.enquiry.id)
  }

  onFetchScore = () => {
    store.emit('enquiry:lookup_score', this.props.enquiry.id)
  }

  onSelectCallOutcome = (value) => {
    store.emit('enquiry:track_call_outcome', this.props.enquiry.id, value)
  }

  onCopyEmail = (email) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(email).then(() => {
        toastr.success('Copied to clipboard.')
      })
    }
  }

  onOpenEmail = () => {
    store.emit('ui:mailer:open', {
      related_to_type: 'Enquiry',
      related_to_id: this.props.enquiry.id
    })
  }

  onOpenWhatsapp = () => {
    store.emit('ui:whatsapper:open', {
      related_to_type: 'Enquiry',
      related_to_id: this.props.enquiry.id,
      recipient_type: 'Customer',
      recipient_id: this.props.enquiry.customer.id
    })
  }

  renderAutomaticTrackButton = () => {
    if (this.props.enquiry.actions.can_enable_automatic_track)
      return <EnableAutomaticTrackButton enquiry_id={this.props.enquiry.id} />
    else if (this.props.enquiry.actions.can_disable_automatic_track)
      return <DisableAutomaticTrackButton enquiry_id={this.props.enquiry.id} />
    else if (this.props.enquiry.in_automatic_track)
      return (
        <Button variant="secondary" size="sm" disabled title="Autotrack is enabled">
          <Hourglass className="size-4 animate-spin"/> AUTO
        </Button>
      )

    return null
  }

  getLessonPlanTabName = (lesson_plan) => {
    const limit = 20
    let name = lesson_plan.learner_names.join(', ')

    if (name.length > limit) {
      name = `${name.substring(0, limit)}... -`
    }
    else if (name.length === 0) {
      name = "Lesson Plan"
    }
    else {
      name = `${name} -`
    }

    return `${name} ${lesson_plan.tutoring_job.reference_number}`
  }

  getTabOptions = () => {
    const enquiry = this.props.enquiry

    let tabOptions = _.compact(_.flatten([
      { value: 'overview', name: 'Overview' },
      { value: 'activity', name: 'Activity' },
      { value: 'learners', name: 'Learners' }
    ]))

    _.each(enquiry.lesson_plans, (lesson_plan) => {
      tabOptions.push({
        value: `lp${lesson_plan.id}`,
        name: this.getLessonPlanTabName(lesson_plan)
      })
    })

    if (!_.isEmpty(enquiry.related_resources)) {
      const count = enquiry.related_resources.length
      tabOptions.push({
        value: 'related_enquiries',
        name: `${count} Related ${count === 1 ? 'Enquiry' : 'Enquiries'}`
      })
    }

    tabOptions.push({ value: 'orders', name: 'Orders' })

    if (enquiry.actions.can_handover || enquiry.actions.can_deactivate) {
      tabOptions.push({ value: 'close', name: 'Close' })
    }

    return tabOptions
  }

  render() {
    const enquiry = this.props.enquiry
    const tabOptions = this.getTabOptions()

    return (
      <div className="dark flex flex-col gap-4 bg-slate-700 px-6 pt-6 text-foreground">
        <div className="flex flex-wrap items-start gap-4">
          <div className="min-w-0 flex-1">
            <Customer
              enquiry_id={enquiry.id}
              customer={enquiry.customer}
              enquiry_customer={enquiry.enquiry_customer}
            />
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {enquiry.links.customer ?
                <Badge asChild variant="secondary" size="md">
                  <a href={enquiry.links.customer} target="_blank">Customer {enquiry.customer.id}</a>
                </Badge>
                : null
              }
              {enquiry.customer.email_address ?
                <Badge variant="secondary" size="md" className="cursor-pointer" onClick={() => this.onCopyEmail(enquiry.customer.email_address)}><Mail className="size-3.5"/>{enquiry.customer.email_address}</Badge>
                : null
              }
              {enquiry.customer.email_cc ?
                <Badge variant="secondary" size="md" className="cursor-pointer" onClick={() => this.onCopyEmail(enquiry.customer.email_cc)}>CC: {enquiry.customer.email_cc}</Badge>
                : null
              }
              {enquiry.country ?
                <Badge variant="secondary" size="md" className="cursor-pointer" onClick={this.onIpAddressClick}>
                  <CountryFlag country_code={enquiry.country_code}/> {enquiry.country.name}
                </Badge>
                : null
              }
              {(!_.isEmpty(enquiry.coachbit_orders)) ?
                <CoachbitOrders coachbit_orders={enquiry.coachbit_orders} link={enquiry.links.customer}/>
                : null
              }
              {enquiry.links.lead ?
                <Badge asChild variant="secondary" size="md">
                  <a href={enquiry.links.lead} target="_blank">Lead {enquiry.lead_id}</a>
                </Badge>
                : null
              }
              <Badge variant="secondary" size="md" className="cursor-pointer" onClick={this.onFetchScore}>Score: {enquiry.score}</Badge>
              {enquiry.classification ?
                <Badge variant="secondary" size="md">{enquiry.classification}</Badge>
                : null
              }
              {_.map(enquiry.labels, function(label) {
                return <EnquiryLabelsPartial key={label.id} label={label} size="md"/>
              })}
              {enquiry.local_time ?
                <LocalTime local_time={enquiry.local_time}/>
                : null
              }
              {_.map(enquiry.scheduled_calls, (scheduled_call) => {
                return (
                  <Badge key={scheduled_call.id} size="md">
                    {scheduled_call.manager_name} to call <CalendarTime date={scheduled_call.start_time}/>
                  </Badge>
                )
              })}
            </div>
          </div>
          <div className="ml-auto">
            <div className="flex flex-wrap items-center justify-end gap-1.5">
              <CallOutcome onSelect={this.onSelectCallOutcome}/>
              <PipelineStatusButton
                enquiry_id={enquiry.id}
                workflow_state={enquiry.workflow_state}
                workflow_states={enquiry.meta.available_workflow_states}
              />
              {this.renderAutomaticTrackButton()}
              {enquiry.priority ?
                <DisablePriorityButton enquiry_id={enquiry.id} />
                : null
              }
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-end gap-1.5">
              <CallButton mobile_number={enquiry.customer.mobile_number} />
              <Button asChild size="sm" variant="outline" title="Open in Customer.io">
                <a href={enquiry.links.customerio} target="_blank">
                  <SvgIcon icon="customerio"/>
                </a>
              </Button>
              {enquiry.links.messagebird &&
                <Button
                  asChild
                  size="sm"
                  variant="outline"
                  title={enquiry.links.messagebird_open ? 'Open ticket in Inbox' : 'Open in Inbox'}
                  className={enquiry.links.messagebird_open ? 'ring-1 ring-sky-400/50 shadow-[0_0_6px_1px_rgba(56,189,248,0.3)]' : undefined}
                >
                  <a href={enquiry.links.messagebird} target="_blank" rel="noopener noreferrer">
                    <Inbox/>
                  </a>
                </Button>
              }
              <WhatsappButton onClick={this.onOpenWhatsapp}/>
              <SmsButton />
              <EmailButton onEmail={this.onOpenEmail}/>
            </div>
            {enquiry.in_placement ?
              <div className="text-right">
                <Badge variant="warning" className="mt-2 h-auto p-2 font-extrabold">
                  One Or More Lesson Plans In Placement
                </Badge>
              </div>
              : null
            }
          </div>
        </div>
        {this.props.ui.enquiry.smser.open ?
          <Modal title="Send SMS" size="modal-lg" onClose={() => store.emit('enquiry:sms:close')}>
            <Smser value={this.props.ui.enquiry.smser.content} />
          </Modal>
          : null
        }
        <Tabs value={this.props.ui.enquiry.tab_key} onValueChange={this.onTabClick}>
          <ScrollableTabsList variant="line" className="bg-transparent pb-0 [&_[data-slot=tabs-trigger]]:rounded-t-md! [&_[data-slot=tabs-trigger]]:rounded-b-none! [&_[data-state=active]]:bg-[#f1f3f3]! [&_[data-state=active]]:text-slate-900! [&_[data-state=active]]:after:opacity-0!">
            {_.map(tabOptions, (tabOption) => {
              return (
                <TabsTrigger key={tabOption.value} value={tabOption.value}>{tabOption.name}</TabsTrigger>
              )
            })}
          </ScrollableTabsList>
        </Tabs>
      </div>
    )
  }
}

export default EnquiryHeader
